// Platform builtins for the Go backend.
//
// The `builtin fn`s declared in the standard library (`stdlib/*.stm`) — `print` /
// `str` / the math helpers, file IO, and TCP networking — are "declared in STM,
// implemented natively". This module is that native implementation for the Go
// backend: the single place where the backend plugs into the host platform
// (`fmt`, `os`, `net`, and the runtime helpers in `runtime.ts`).
//
// It is kept out of `gogen.ts` on purpose. `gogen.ts` lowers the *language* — its
// statements, expressions, dispatch, transactions, and store — and should not
// grow a new arm every time a builtin reaches for another OS facility. Everything
// this module needs from the backend arrives through the small `BuiltinCtx`
// below, so it never sees (or can touch) that language-lowering machinery.
//
// Each emitter returns a self-contained Go expression. A fallible IO/net op
// builds the monomorphized `Res` instance the call was specialized to
// (`ctx.goType(x.type)`, e.g. `Res__IOError_string`) with `Ok`/`Err`, so a
// program `match`es on the result — nothing throws across the platform boundary.

import { CoreExpr } from "../../middle-end/core.js";
import { FLOAT, INT, Type } from "../../frontend/types.js";
import { GoGenError } from "./errors.js";

/** The host facilities a builtin can pull in. Flagging one makes `gogen` emit an
 *  import and/or a runtime block: `net`/`ioPkg` are imports; `net`/`fileHandle`
 *  both pull in the shared handle registry. */
export type HostPkg = "math" | "rand" | "net" | "fileHandle" | "ioPkg" | "readLine";

/** What a builtin emitter needs from the backend, and nothing more: lower a Core
 *  argument expression, reconcile scalar types, name a monomorphized Go type, and
 *  record that a host package is used. Deliberately excludes statements, dispatch,
 *  the store, and every other language-lowering concern that lives in `gogen.ts`. */
export interface BuiltinCtx {
  /** Lower a Core (argument) expression to a Go expression, in the caller's env. */
  emit(x: CoreExpr): string;
  /** Widen a scalar Go expression from one type to another (int → float). */
  coerce(code: string, from: Type, to: Type): string;
  /** The Go type for a Core type (e.g. a call's monomorphized `Res` name). */
  goType(t: Type): string;
  /** Mark a host package as used, so its import (and net's runtime) is emitted. */
  use(pkg: HostPkg): void;
}

/** Lower a call to a `builtin fn` (a Core `builtin` node) to a Go expression.
 *  `gogen` delegates every such node here; this switch is the backend's entire
 *  platform surface. An unknown builtin throws `GoGenError` — the same fallback
 *  boundary as an unlowered construct. */
export function emitBuiltin(x: Extract<CoreExpr, { e: "builtin" }>, ctx: BuiltinCtx): string {
  const args = x.args.map((a) => ctx.emit(a));
  // An argument coerced to a target scalar type (int↔float widening).
  const arg = (i: number, to: Type): string => ctx.coerce(args[i]!, x.args[i]!.type, to);
  switch (x.name) {
    case "print": return `__print(${args.join(", ")})`;
    // `assert cond, msg` — soft, like the interpreter's: it records the verdict
    // and the program carries on. There is no trace here to record it *in*, so a
    // failure goes to stderr and sets the process's exit code, leaving stdout to
    // the program's own `print`s (which is what the backends are compared on).
    case "assert": return `__assert(${args[0]}, ${args[1]})`;
    // `str(x)` renders any value — its `Show` repr (dispatched inside __show) or
    // the default rendering.
    case "str": return `__show(${args[0]})`;
    case "sleep": return `__sleep(${arg(0, FLOAT)})`;
    // `now()` — current wall-clock time as an ISO-8601 string (RFC 3339, a strict
    // ISO-8601 profile), matching the interpreter's `toISOString`. `time` is always
    // imported (see gogen's `emitImports`), so no `use` flag.
    case "now": return `time.Now().UTC().Format("2006-01-02T15:04:05.000Z07:00")`;
    // The process-environment seam (see `io.stm`). The arguments map to the
    // compiled binary's `os.Args` minus argv[0] (the program name); the environment
    // to `os.Environ()` (raw `NAME=value` strings). The indexed accessors are
    // bounds-checked, panicking out of range like `str.charAt`. `os` is always
    // imported (see gogen's `emitImports`), so no `use` flag.
    case "_argCount": return `int64(len(os.Args) - 1)`;
    case "_argAt":
      return (
        `func() string { __i := int(${arg(0, INT)}); ` +
        `if __i < 0 || __i+1 >= len(os.Args) { panic("_argAt: index out of range") }; ` +
        `return os.Args[__i+1] }()`
      );
    case "_envCount": return `int64(len(os.Environ()))`;
    case "_envEntry":
      return (
        `func() string { __e := os.Environ(); __i := int(${arg(0, INT)}); ` +
        `if __i < 0 || __i >= len(__e) { panic("_envEntry: index out of range") }; ` +
        `return __e[__i] }()`
      );
    case "rand": ctx.use("rand"); return `rand.Float64()`;
    case "randInt": ctx.use("rand"); return `int64(rand.Intn(int(${arg(0, INT)})))`;
    case "abs":
      ctx.use("math");
      return x.args[0]!.type.k === "int" ? `__absI(${args[0]})` : `math.Abs(${arg(0, FLOAT)})`;
    case "sqrt": ctx.use("math"); return `math.Sqrt(${arg(0, FLOAT)})`;
    case "floor": ctx.use("math"); return `int64(math.Floor(${arg(0, FLOAT)}))`;
    // `fileExists` — the one path-based file query, a self-contained IIFE building
    // the monomorphized `Res` (`x.type`): `Ok(true)`/`Ok(false)`, or `Err` on a stat
    // error other than "not found".
    case "fileExists":
      return emitFileExists(args, ctx.goType(x.type));
    // Handle-based file IO. `open` is a free function; the file ops are `builtin`
    // methods lowered to `File_method` (so `self` — the handle — leads `args`).
    // `stdin`/`stdout`/`stderr` are the std streams as `File` handles (fixed handles
    // 0/1/2, pre-seeded in the registry). They work through the shared handle
    // registry; `read` also needs `io.ReadAll`.
    case "open": case "File_write": case "File_close":
    case "stdin": case "stdout": case "stderr":
      ctx.use("fileHandle");
      return emitFile(x.name, args, ctx.goType(x.type));
    case "File_read":
      ctx.use("fileHandle");
      ctx.use("ioPkg");
      return emitFile(x.name, args, ctx.goType(x.type));
    case "File_readLine":
      // Line reading needs a persistent buffered reader per handle (see the
      // line-reader runtime) and `io.EOF`.
      ctx.use("fileHandle");
      ctx.use("ioPkg");
      ctx.use("readLine");
      return emitFile(x.name, args, ctx.goType(x.type));
    // TCP networking. `listen`/`connect` are free functions; the socket ops are
    // `builtin` methods, lowered to a builtin named `Type_method` (`conn.send` ->
    // `TcpConn_send`), so `self` leads `args` and the two `close`s stay distinct.
    // Each builds the monomorphized `Res` (`x.type`) it was instantiated to and
    // works through the shared handle registry.
    case "listen": case "connect":
    case "TcpConn_send": case "TcpConn_recv": case "TcpConn_close":
    case "TcpListener_accept": case "TcpListener_close":
      ctx.use("net");
      return emitNet(x.name, args, ctx.goType(x.type));
    // String & char primitives (the native part of `impl str`/`impl char`). Index
    // by Unicode code point (`[]rune`), so a string's length/`charAt` agree with
    // `char` — no host package needed, these are pure Go.
    case "str_len": case "str_charAt": case "str_slice": case "str_indexOf": case "char_code":
      return emitStrChar(x.name, args);
    // `Eq.eq` on a scalar (the native part of the prelude's `impl Eq for …`).
    // Declared `builtin` there because writing it as `self == other` would recurse
    // through the operator it defines; here that is exactly what it lowers to, so
    // `1.eq(1)` and `1 == 1` emit the same Go comparison. Every scalar STM compares
    // maps to a comparable Go type, so one case covers all five.
    case "int_eq": case "float_eq": case "char_eq": case "str_eq": case "bool_eq":
      return `(${args[0]} == ${args[1]})`;
    // Arithmetic on a scalar (the native part of `impl Add / Sub / Mul / Div for
    // …`). Declared `builtin` there for the same reason as `eq`: `self OP other`
    // would recurse if the operator resolved through its trait for scalars; here
    // that is exactly what it lowers to, so `6.mul(7)` and `6 * 7` emit the same Go
    // `*`. Each maps to its native Go operator (Go's `/` truncates int64 toward zero,
    // like the interpreter); `+` also covers string concatenation.
    case "int_add": case "float_add": case "str_add":
      return `(${args[0]} + ${args[1]})`;
    case "int_sub": case "float_sub":
      return `(${args[0]} - ${args[1]})`;
    case "int_mul": case "float_mul":
      return `(${args[0]} * ${args[1]})`;
    case "int_div": case "float_div":
      return `(${args[0]} / ${args[1]})`;
    case "orElse":
      // The `select` / composable-blocking primitive. Its two arguments are
      // retry-capable thunks — closures taking the ambient `tx` (emitted by the
      // "closure" case in gogen). `orElse` runs the first and, if it `retry`s, rolls
      // back its tentative writes and runs the second (see `orElse` in runtime.ts).
      // Go infers the result type `T` from the thunks, which share one return type.
      return `orElse(tx, ${args[0]}, ${args[1]})`;
    default: throw new GoGenError(`builtin \`${x.name}\` not lowered by the Go backend`);
  }
}

/** A string/char method (`impl str`/`impl char`) as a self-contained Go
 *  expression. Everything indexes by Unicode code point (`[]rune`), so `len` /
 *  `charAt` / `indexOf` are code-point-based and agree with `char`. `self` is
 *  `args[0]`; the int arguments are `int64` (cast to `int` for slicing). */
function emitStrChar(name: string, args: string[]): string {
  const self = args[0]!;
  switch (name) {
    case "str_len":
      return `int64(len([]rune(${self})))`;
    case "str_charAt":
      // Bounds-checked, like the interpreter (panics on an out-of-range index).
      return (
        `func() rune { __r := []rune(${self}); __i := int(${args[1]}); ` +
        `if __i < 0 || __i >= len(__r) { panic("charAt: index out of range") }; return __r[__i] }()`
      );
    case "str_slice":
      // Half-open and clamped to [0, len], so it never panics.
      return (
        `func() string { __r := []rune(${self}); __n := len(__r); __s := int(${args[1]}); __e := int(${args[2]}); ` +
        `if __s < 0 { __s = 0 }; if __s > __n { __s = __n }; if __e < __s { __e = __s }; if __e > __n { __e = __n }; ` +
        `return string(__r[__s:__e]) }()`
      );
    case "str_indexOf":
      // First code-point index of char `args[1]`, or -1.
      return `func() int64 { for __i, __c := range []rune(${self}) { if __c == ${args[1]} { return int64(__i) } }; return -1 }()`;
    default: // char_code
      return `int64(${self})`;
  }
}

/** `fileExists(path)` as a self-contained Go expression, building the
 *  monomorphized `Res` (`res`, e.g. `Res__IOError_bool`). `os.IsNotExist` is the
 *  real "no"; any other stat error means we couldn't tell, so it's an `Err`, not a
 *  false "absent". */
function emitFileExists(args: string[], res: string): string {
  return (
    `func() ${res} { _, __e := os.Stat(${args[0]}); ` +
    `if __e == nil { return ${res}_Ok{ok: true} }; ` +
    `if os.IsNotExist(__e) { return ${res}_Ok{ok: false} }; ` +
    `return ${res}_Err{err: IOError{message: __e.Error()}} }()`
  );
}

/** A TCP-networking builtin as a self-contained Go expression, building the
 *  monomorphized `Res` instance (`res`, e.g. `Res__IOError_TcpConn`) with
 *  `Ok`/`Err`. `listen`/`connect` are free functions; the rest are `builtin`
 *  methods lowered to `Type_method` (so `self` — the handle-carrying value — leads
 *  `args`). A `TcpListener`/`TcpConn` is a value type wrapping an int handle; the
 *  real net.Listener/net.Conn is looked up in the net runtime's registry
 *  (`__hPut`/`__hGet`/`__hDrop`). A read/dial/accept failure becomes
 *  `Err(IOError{message})`; a bad handle (never expected from correct code) does
 *  too, so the runtime stays panic-free. */
function emitNet(name: string, args: string[], res: string): string {
  const fail = `${res}_Err{err: IOError{message: __e.Error()}}`;
  const badHandle = (what: string) => `${res}_Err{err: IOError{message: "invalid ${what} handle"}}`;
  // The method ops take the handle from `self` (args[0]); bind it once (the arg is
  // usually a plain variable, but binding avoids re-evaluating a side-effecting one).
  const h = `__h := ${args[0]}.handle; `;
  switch (name) {
    case "listen":
      return `func() ${res} { __l, __e := net.Listen("tcp", ${args[0]}); if __e != nil { return ${fail} }; return ${res}_Ok{ok: TcpListener{handle: __hPut(__l)}} }()`;
    case "connect":
      return `func() ${res} { __c, __e := net.Dial("tcp", ${args[0]}); if __e != nil { return ${fail} }; return ${res}_Ok{ok: TcpConn{handle: __hPut(__c)}} }()`;
    case "TcpListener_accept":
      return (
        `func() ${res} { ${h}__l, __ok := __hGet(__h).(net.Listener); if !__ok { return ${badHandle("listener")} }; ` +
        `__c, __e := __l.Accept(); if __e != nil { return ${fail} }; return ${res}_Ok{ok: TcpConn{handle: __hPut(__c)}} }()`
      );
    case "TcpConn_send":
      return (
        `func() ${res} { ${h}__c, __ok := __hGet(__h).(net.Conn); if !__ok { return ${badHandle("connection")} }; ` +
        `if _, __e := __c.Write([]byte(${args[1]})); __e != nil { return ${fail} }; return ${res}_Ok{ok: Unit{}} }()`
      );
    case "TcpConn_recv":
      return (
        `func() ${res} { ${h}__c, __ok := __hGet(__h).(net.Conn); if !__ok { return ${badHandle("connection")} }; ` +
        `__buf := make([]byte, 4096); __n, __e := __c.Read(__buf); if __e != nil { return ${fail} }; return ${res}_Ok{ok: string(__buf[:__n])} }()`
      );
    case "TcpConn_close":
      return (
        `func() ${res} { ${h}__c, __ok := __hGet(__h).(net.Conn); if !__ok { return ${badHandle("connection")} }; ` +
        `__hDrop(__h); if __e := __c.Close(); __e != nil { return ${fail} }; return ${res}_Ok{ok: Unit{}} }()`
      );
    default: // TcpListener_close
      return (
        `func() ${res} { ${h}__l, __ok := __hGet(__h).(net.Listener); if !__ok { return ${badHandle("listener")} }; ` +
        `__hDrop(__h); if __e := __l.Close(); __e != nil { return ${fail} }; return ${res}_Ok{ok: Unit{}} }()`
      );
  }
}

/** A handle-based file builtin as a self-contained Go expression, building the
 *  monomorphized `Res` instance (`res`). `open` is a free function that maps the
 *  `FileMode` value to `os.OpenFile` flags and registers the `*os.File`; the rest
 *  are `builtin` methods lowered to `File_method` (so `self` — the handle — leads
 *  `args`). The real `*os.File` lives in the shared handle registry. A bad handle
 *  (never expected from correct code) becomes an `Err`, so the runtime stays
 *  panic-free. */
function emitFile(name: string, args: string[], res: string): string {
  const fail = `${res}_Err{err: IOError{message: __e.Error()}}`;
  const badFile = `${res}_Err{err: IOError{message: "invalid file handle"}}`;
  const err = (msg: string) => `${res}_Err{err: IOError{message: "${msg}"}}`;
  // The method ops take the fd from `self` (args[0]); bind it, then resolve `*os.File`
  // (std streams 0/1/2 are pre-seeded to os.Stdin/Stdout/Stderr in the registry).
  const h = `__h := ${args[0]}.handle; `;
  const get = `__f, __ok := __hGet(__h).(*os.File); if !__ok { return ${badFile} }; `;
  switch (name) {
    // The std streams as `File` handles (fixed 0/1/2; see the seeded registry).
    case "stdin": return `File{handle: 0}`;
    case "stdout": return `File{handle: 1}`;
    case "stderr": return `File{handle: 2}`;
    case "open":
      // Map the FileMode variant to os flags via a type switch, then open. The
      // mode is boxed into the `FileMode` interface first, so `.(type)` works even
      // when the argument is a concrete variant literal (`FileMode_Write{}`).
      return (
        `func() ${res} { var __mode FileMode = ${args[1]}; var __flag int; switch __mode.(type) { ` +
        `case FileMode_Read: __flag = os.O_RDONLY; ` +
        `case FileMode_Write: __flag = os.O_WRONLY | os.O_CREATE | os.O_TRUNC; ` +
        `default: __flag = os.O_APPEND | os.O_CREATE | os.O_WRONLY }; ` +
        `__f, __e := os.OpenFile(${args[0]}, __flag, 0644); if __e != nil { return ${fail} }; ` +
        `return ${res}_Ok{ok: File{handle: __hPut(__f)}} }()`
      );
    case "File_read":
      // Reading a write-only std stream (stdout/stderr) is a usage error.
      return (
        `func() ${res} { ${h}if __h == 1 || __h == 2 { return ${err("cannot read from stdout/stderr")} }; ` +
        `${get}__d, __e := io.ReadAll(__f); if __e != nil { return ${fail} }; return ${res}_Ok{ok: string(__d)} }()`
      );
    case "File_readLine":
      // Read one line through a persistent buffered reader (so a partial buffered
      // read isn't lost between calls). `Ok(Some(line))` without the terminator, or
      // `Ok(None)` at end of input.
      return (
        `func() ${res} { ${h}if __h == 1 || __h == 2 { return ${err("cannot read from stdout/stderr")} }; ` +
        `__r := __lineReader(__h); if __r == nil { return ${err("invalid file handle")} }; ` +
        `__line, __e := __r.ReadString('\\n'); if __e != nil && __e != io.EOF { return ${fail} }; ` +
        `if len(__line) == 0 { return ${res}_Ok{ok: Option__string_None{}} }; ` +
        `if __line[len(__line)-1] == '\\n' { __line = __line[:len(__line)-1] }; ` +
        `if len(__line) > 0 && __line[len(__line)-1] == '\\r' { __line = __line[:len(__line)-1] }; ` +
        `return ${res}_Ok{ok: Option__string_Some{value: __line}} }()`
      );
    case "File_write":
      // Writing the read-only std stream (stdin) is a usage error.
      return (
        `func() ${res} { ${h}if __h == 0 { return ${err("cannot write to stdin")} }; ` +
        `${get}if _, __e := __f.WriteString(${args[1]}); __e != nil { return ${fail} }; return ${res}_Ok{ok: Unit{}} }()`
      );
    default: // File_close
      // Closing a std stream is a harmless no-op — the process keeps its streams.
      return (
        `func() ${res} { ${h}if __h <= 2 { return ${res}_Ok{ok: Unit{}} }; ` +
        `${get}__hDrop(__h); if __e := __f.Close(); __e != nil { return ${fail} }; return ${res}_Ok{ok: Unit{}} }()`
      );
  }
}
